import { Layout } from "@/components/layout";
import { SEOHead } from "@/components/seo-head";
import { WEBINARS } from "@/lib/content";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { Calendar, PlayCircle, Users } from "lucide-react";

export default function WebinarsPage() {
  const hasWebinars = WEBINARS.length > 0;

  return (
    <Layout>
      <SEOHead
        title="Webinars | ClauseArk"
        description="Live sessions and on-demand recordings from the ClauseArk team."
      />

      <section className="pt-24 pb-20 md:pt-32 md:pb-24 border-b border-border/50 bg-slate-50 dark:bg-navy/30">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl"
          >
            <h1 className="text-display mb-6 text-navy dark:text-white">
              Webinars
            </h1>
            <p className="text-body-lg text-slate-600 dark:text-slate-300">
              Live sessions and on-demand recordings from the ClauseArk team.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 md:py-32">
        <div className="container mx-auto px-4 md:px-6 max-w-5xl">
          {hasWebinars ? (
            <div className="space-y-8">
              {WEBINARS.map((webinar, i) => {
                const isUpcoming = webinar.status === "Upcoming";

                return (
                  <motion.div
                    key={webinar.slug}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.1 }}
                    className="bg-card rounded-2xl border p-6 md:p-8 shadow-sm flex flex-col md:flex-row gap-8 items-start"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-4">
                        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                          isUpcoming
                            ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300'
                            : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
                        }`}>
                          {webinar.status}
                        </span>
                        <div className="flex items-center gap-1.5 text-sm text-muted-foreground font-medium">
                          <Calendar className="h-4 w-4" />
                          {webinar.date}
                        </div>
                      </div>

                      <h2 className="text-xl md:text-2xl font-bold mb-4 text-navy dark:text-white">
                        {webinar.title}
                      </h2>

                      <p className="text-slate-600 dark:text-slate-400 mb-6 leading-relaxed">
                        {webinar.excerpt}
                      </p>

                      <div className="flex items-start gap-2">
                        <Users className="h-5 w-5 text-slate-400 shrink-0 mt-0.5" />
                        <div className="text-sm text-slate-600 dark:text-slate-400">
                          <span className="font-semibold text-foreground">Speakers:</span> {webinar.speakers.join(", ")}
                        </div>
                      </div>
                    </div>

                    <div className="w-full md:w-auto md:shrink-0 flex flex-col gap-3 pt-2">
                      <Button className="w-full md:w-48" variant={isUpcoming ? "default" : "outline"}>
                        {isUpcoming ? "Register" : (
                          <span className="flex items-center gap-2">
                            <PlayCircle className="h-4 w-4" /> Watch recording
                          </span>
                        )}
                      </Button>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <div className="max-w-xl mx-auto text-center py-12">
              <div className="h-14 w-14 mx-auto rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500 mb-6">
                <PlayCircle className="h-7 w-7" />
              </div>
              <h2 className="text-h3 text-navy dark:text-white mb-3">No webinars yet</h2>
              <p className="text-body text-slate-600 dark:text-slate-400 mb-8">
                We don't have any live sessions or recordings to share right now. When we run one, you'll find it here.
              </p>
              <Link href="/demo">
                <Button size="lg" className="bg-brand-gradient text-white border-0 no-default-hover-elevate">
                  Get a live walkthrough
                </Button>
              </Link>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}
